import { Component, inject, input, OnChanges, output, signal, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { UserVendorContact } from "../../../../../../../core/database/db";
import { UserVendorContactService } from "../../../../../../../domains/vendors/services/user-vendor-contact.service";
import { UiToastService } from "../../../../../../../shared/components/toast/ui-toast.service";
import { LoggerService } from "../../../../../../../shared/services/logger.service";
import { BottomSheetComponent } from "../../../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { EditVendorContactAction, EditVendorContactComponent, EditVendorContactMode, EditVendorContactResult } from "../edit-vendor-contact/edit-vendor-contact.component";
import { OpenItemsDataService } from "../../../services/open-items-data.service";

@Component({
  selector: "app-send-to-contact",
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, BottomSheetComponent, EditVendorContactComponent],
  templateUrl: "./send-to-contact.component.html",
  styleUrls: ["./send-to-contact.component.scss"]
})
export class SendToContactComponent implements OnChanges {
  openItemsDataService = inject(OpenItemsDataService);

  contacts = input.required<UserVendorContact[]>();
  externalVendorId = input.required<number>();
  externalFinancialCommunityId = input.required<number>();
  vendorName = input<string>("");
  showBackButton = input<boolean>(true);
  showAnimations = input<boolean>(true);
  preCheckedContacts = input<UserVendorContact[]>();

  contactAdded = output<UserVendorContact>();
  contactUpdated = output<UserVendorContact>();
  contactDeleted = output<UserVendorContact>();
  selectionChange = output<UserVendorContact[]>();

  showContactSheet = signal<boolean>(false);
  isContactFormOpen = signal<boolean>(false);
  formMode = signal<EditVendorContactMode>("add");
  editingContact = signal<UserVendorContact | null>(null);
  selectedContactIds = signal<Set<number>>(new Set());
  isSaving = signal<boolean>(false);
  loadingAction = signal<EditVendorContactAction | null>(null);
  formResetVersion = signal<number>(0);

  constructor(
    private userVendorContactService: UserVendorContactService,
    private toastService: UiToastService,
    private logger: LoggerService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["externalVendorId"]) {
      this.closeContactSheet();
      this.selectedContactIds.set(new Set());
    }
    if (changes["contacts"] && this.contacts !== undefined) {
      const previousContacts = changes["contacts"].previousValue as UserVendorContact[] | undefined;
      const previousIds = new Set(
        (previousContacts ?? [])
          .map(contact => contact.id)
          .filter((id): id is number => id !== null && id !== undefined)
      );
      const currentIds = new Set(
        this.contacts()
          .map(contact => contact.id)
          .filter((id): id is number => id !== null && id !== undefined)
      );
      const selectedIds = new Set(
        Array.from(this.selectedContactIds()).filter(id => currentIds.has(id))
      );
      currentIds.forEach(id => {
        if (!previousIds.has(id)) {
          selectedIds.add(id);
        }
      });
      if (this.preCheckedContacts()) {
        this.selectedContactIds.set(new Set(this.preCheckedContacts()!.map(contact => contact.id!)));
      } else {
        this.selectedContactIds.set(selectedIds);
        this.emitSelection();
      }
    }
  }

  isContactSelected(contact: UserVendorContact): boolean {
    return contact.id !== null && contact.id !== undefined && this.selectedContactIds().has(contact.id);
  }

  onContactSelectionChange(contact: UserVendorContact, checked: boolean): void {
    const ids = new Set(this.selectedContactIds());
    if (checked && contact.id !== null && contact.id !== undefined) {
      ids.add(contact.id);
    } else if (contact.id !== null && contact.id !== undefined) {
      ids.delete(contact.id);
    }
    this.selectedContactIds.set(ids);
    this.emitSelection();
  }

  onAddContact(): void {
    if (this.isSaving()) {
      return;
    }
    this.editingContact.set(null);
    this.formMode.set("add");
    this.openContactSheet();
  }

  onEditContact(contact: UserVendorContact): void {
    if (this.isSaving()) {
      return;
    }
    this.editingContact.set(contact);
    this.formMode.set("edit");
    this.openContactSheet();
  }

  async onContactSave(result: EditVendorContactResult): Promise<void> {
    if (this.externalVendorId === null || this.isSaving()) {
      return;
    }
    this.loadingAction.set("save");
    this.isSaving.set(true);
    try {
      if (result.mode === "add") {
        const newContact = await this.persistAdd(result.email);
        this.contactAdded.emit(newContact);
      } else if (result.mode === "edit") {
        const updated = await this.persistEdit(result.email);
        if (updated) {
          this.contactUpdated.emit(updated);
        }
      }
      this.closeContactSheet();
    } catch (error) {
      this.logger.error("Error saving contact", error);
      this.toastService.error("Unable to save contact");
    } finally {
      this.isSaving.set(false);
      this.loadingAction.set(null);
    }
  }

  async onContactSaveAndAddAnother(result: EditVendorContactResult): Promise<void> {
    if (this.externalVendorId === null || this.isSaving()) {
      return;
    }
    this.loadingAction.set("saveAndAddAnother");
    this.isSaving.set(true);
    try {
      const newContact = await this.persistAdd(result.email);
      this.contactAdded.emit(newContact);
      this.toastService.success("Contact saved");
      this.editingContact.set(null);
      this.formMode.set("add");
      this.formResetVersion.update(version => version + 1);
    } catch (error) {
      this.logger.error("Error saving contact", error);
      this.toastService.error("Unable to save contact");
    } finally {
      this.isSaving.set(false);
      this.loadingAction.set(null);
    }
  }

  async onContactDelete(contact: UserVendorContact): Promise<void> {
    if (this.isSaving()) {
      return;
    }
    this.loadingAction.set("delete");
    this.isSaving.set(true);
    try {
      await this.openItemsDataService.deleteContactFromVendor(contact.id!);
      this.contactDeleted.emit(contact);
      this.closeContactSheet();
    } catch (error) {
      this.logger.error("Error deleting contact", error);
      this.toastService.error("Unable to delete contact");
    } finally {
      this.isSaving.set(false);
      this.loadingAction.set(null);
    }
  }

  onContactCancel(): void {
    if (this.isSaving()) {
      return;
    }
    this.closeContactSheet();
  }

  onContactSheetOpenChange(isOpen: boolean): void {
    if (!isOpen && !this.isSaving()) {
      this.closeContactSheet();
    }
  }

  onContactFormTransitionEnd(event: TransitionEvent): void {
    if (
      event.propertyName !== "transform"
      || event.target !== event.currentTarget
      || this.isContactFormOpen()
    ) {
      return;
    }

    this.finishClosingContactSheet();
  }

  private async persistAdd(email: string): Promise<UserVendorContact> {
    const contactToAdd: Omit<UserVendorContact, "id" | "deleted" | "updatedAt" | "syncStatus"> = {
      externalVendorId: this.externalVendorId(),
      externalFinancialCommunityId: this.externalFinancialCommunityId(),
      email
    };

    const savedContact = await this.openItemsDataService.addContactToVendor(contactToAdd);

    const ids = new Set(this.selectedContactIds());
    ids.add(savedContact.id!);
    this.selectedContactIds.set(ids);

    return savedContact;
  }

  private async persistEdit(email: string): Promise<UserVendorContact | null> {
    const contact = this.editingContact();
    if (!contact) {
      return null;
    }

    const contactUpdates: Pick<UserVendorContact, "email"> = {
      email
    };

    const updatedContact = await this.openItemsDataService.updateContactForVendor(contact, contactUpdates);

    return updatedContact;
  }

  private openContactSheet(): void {
    this.showContactSheet.set(false);
    this.showContactSheet.set(true);
    this.isContactFormOpen.set(true);
  }

  private closeContactSheet(): void {
    if (!this.showContactSheet()) {
      this.finishClosingContactSheet();
      return;
    }

    if (!this.showAnimations()) {
      this.finishClosingContactSheet();
    }

    this.isContactFormOpen.set(false);
  }

  private finishClosingContactSheet(): void {
    this.showContactSheet.set(false);
    this.isContactFormOpen.set(false);
    this.editingContact.set(null);
    this.formMode.set("add");
    this.formResetVersion.update(version => version + 1);
  }

  private emitSelection(): void {
    const ids = this.selectedContactIds();
    const selected = (this.contacts() ?? []).filter(c => c.id !== null && c.id !== undefined && ids.has(c.id!));
    this.selectionChange.emit(selected);
  }
}
